import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import pin from '../Pages/Worldmap/SVG/pin.svg';
import * as api from '../../api';
import shoppingIcon from '../Pages/Worldmap/SVG/2_SHOPPING.png';
import hotelIcon from '../Pages/Worldmap/SVG/3_SONETTE.png';
import officeIcon from '../Pages/Worldmap/SVG/8_OFFICE_STROKE.png';
import restaurantIcon from '../Pages/Worldmap/SVG/5_CUISINE.png';
import culturalIcon from '../Pages/Worldmap/SVG/6_PEINTURE.png';
import residenceIcon from '../Pages/Worldmap/SVG/7_SERRURE.png';
import urbanIcon from '../Pages/Worldmap/SVG/8_BANC.png';
import installationIcon from '../Pages/Worldmap/SVG/9_INSTALLATION_SCENOGRAPHY.png';
import link from '../Utils/Format';
import { useNavigate } from 'react-router-dom';

// Helper function to format category names for display
const formatCategoryForDisplay = (category) => {
    if (category === "Installation/Scenography") {
        return "Installation / Scenography";
    }
    return category;
};

const POINT_ICONS = {
    "Retail": shoppingIcon,
    "Hotel": hotelIcon,
    "Office": officeIcon,
    "Restaurant/bar": restaurantIcon,
    "Cultural space": culturalIcon,
    "Private residence": residenceIcon,
    "Urban space/Transportation": urbanIcon,
    "Installation/Scenography": installationIcon
  };

const WorldmapSection = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 700px;
    @media (max-width: 768px) {
        flex-direction: column-reverse;
        height: 1400px;
    }
`;

const CardWrapper = styled.div`
    flex: 0.6;
    background: #f9f9f9;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    font-family: 'SuisseIntl', sans-serif;
    overflow-y: scroll;

    @media (max-width: 768px) {
        display:none;
    }
`;

const MapWrapper = styled.div`
    flex: 2.4;
    position: relative;
    height: 100%;
    overflow: hidden;
    border-left: solid 1px black;
    border-right: solid 1px black;

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
        pointer-events: ${(props) => (props.isInteractive ? 'auto' : 'none')};
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: auto;
`;

const Section = styled.div`
    border-bottom: 1px solid black;
    padding: 10px;
`;

const CategorySection = styled(Section)`
    display: flex;
    justify-content: space-between;
`;

const CategoryWrapper = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: left;
    gap: 8px;
`;

const CategoryIcon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;
`;

const CategoryText = styled.p`
    font-size: 14px;
    text-align: center;
    color: black;
    margin: 0;
    text-transform: uppercase;
`;

const BlueTitle = styled.strong`
    color: #0000ff;
    font-size: 18px;
    display: block;
    font-weight: 500;
    text-transform: uppercase;
    cursor: pointer;
`;

const BlueArrow = styled.img`
    margin: 0 5px 0 0;
    display: inline-block;
    height: 14px;
`;

const Description = styled.strong`
    font-size: 14px;
    font-weight: 400;
    text-align: left;
    color: black;
    display: block;
    margin-top: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Year = styled.p`
    color: black;
    font-size: 14px;
    font-weight: bold;
    text-align: left;
    margin: 10px 0;
`;

const AddressWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Address = styled.strong`
    font-size: 14px;
    font-weight: 400;
    text-align: left;
    color: black;
`;

const LinksSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
    margin-top: 10px;
    padding: 0 20px;
    margin-bottom: 10px;
`;

const StyledLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.8px;
    padding: 6px 10px;
    background-color: #0039d6;
    border: 2px solid transparent;
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    text-decoration: none;
    text-transform: uppercase;
    transition: all .2s ease-in-out;
    transform: scale(1);

    &:hover {
        background-color: white;
        border: 2px solid #0039d6;
        color: #0039d6;
        filter: drop-shadow(0px 0px 3px #5C81E6);
        transform: scale(1.05);

        img {
            filter: invert(23%) sepia(97%) saturate(1894%) hue-rotate(214deg) brightness(97%) contrast(106%);
        }
    }
`;

const LinkIcon = styled.img`
    width: 20px;
    height: 20px;
    margin-right: 10px;
    filter: brightness(0) invert(1);
`;

const ProjectTitle = styled.div`
    font-weight: bold;
    text-align: center;
    padding: 10px;
    background-color: #f0f0f0;
    border-bottom: 1px solid black;
`;

const Worldmaphome = () => {
    const [isInteractive, setIsInteractive] = useState(false);
    const [monthlyProject, setMonthlyProject] = useState(null);
    const navigate = useNavigate(); // Add this at the top with your other imports

    useEffect(() => {
        // Add message listener for iframe communication
        const handleMessage = (event) => {
            if (event.data.type === 'NAVIGATE') {
                navigate(event.data.path);
            }
        };

        window.addEventListener('message', handleMessage);

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [navigate]);


    useEffect(() => {
        api.GetMonthlyMapProject()
            .then((response) => {
                setMonthlyProject(response.data);
            })
            .catch((error) => {
                console.error('Error fetching monthly project:', error);
            });
    }, []);

    return (
        <div>
            <WorldmapSection>
                <CardWrapper>
                    {monthlyProject && (
                        <>
                            <CardImage 
                                src={monthlyProject.photos_projet?.split(',')[0].trim()} 
                                alt={monthlyProject.titre_affiche} 
                            />
                            <CategorySection>
                                <CategoryWrapper>
                                {monthlyProject.points_of_interest && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'left', gap: '8px' }}>
          <img 
            src={POINT_ICONS[monthlyProject.points_of_interest]} 
            alt={monthlyProject.points_of_interest}
            style={{
              width: '24px',
              height: '24px',
              objectFit: 'contain'
            }}
          />
          <p style={{ fontSize: '14px', textAlign: 'left', color: 'black', margin: 0 , textAlign:'center', textTransform:'uppercase'}}>
            {formatCategoryForDisplay(monthlyProject.points_of_interest)}
          </p>
        </div>
      )}
                                </CategoryWrapper>
                            </CategorySection>

                            <Section>
                                <a href={`https://connectedby.lefrenchdesign.org/company?id=${monthlyProject.mongo_company_id}`} style={{ textDecoration: 'none' }}>
                                    <BlueTitle>
                                        <BlueArrow src="/img/fleche_bleue.png" alt="" />
                                        {monthlyProject.fiche_agence}
                                    </BlueTitle>
                                </a>
                                <Description>{monthlyProject.description_agence_fr}</Description>
                            </Section>

                            <Section>
                                <a href={`https://connectedby.lefrenchdesign.org/project?pid=${monthlyProject.mongo_project_id}&cid=${monthlyProject.mongo_company_id}`} style={{ textDecoration: 'none' }}>
                                    <BlueTitle>
                                        <BlueArrow src="/img/fleche_bleue.png" alt="" />
                                        {monthlyProject.titre_affiche}
                                    </BlueTitle>
                                </a>
                                {monthlyProject.annee_projet && (
                                    <Year>{monthlyProject.annee_projet}</Year>
                                )}
                                <Description>{monthlyProject.description_projet}</Description>
                            </Section>

                            <Section>
                                <AddressWrapper>
                                    <img src={pin} alt="Location Pin" style={{ width: '16px', height: '16px' }} />
                                    <Address>{monthlyProject.adresse}</Address>
                                </AddressWrapper>
                            </Section>

                            <LinksSection>
                                {monthlyProject.site_web && (
                                    <StyledLink href={link(monthlyProject.site_web)} target="_blank" rel="noopener noreferrer">
                                        <LinkIcon src="/img/weblogo.svg" alt="Website" />
                                        <span>Website</span>
                                    </StyledLink>
                                )}
                                {monthlyProject.instagram && (
                                    <StyledLink href={`https://instagram.com/${monthlyProject.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                                        <LinkIcon src="/img/logoinsta.svg" alt="Instagram" />
                                        <span>Instagram</span>
                                    </StyledLink>
                                )}
                                {monthlyProject.email && (
                                    <StyledLink href={`mailto:${monthlyProject.email}`}>
                                        <LinkIcon src="/img/email.svg" alt="Email" />
                                        <span>Email</span>
                                    </StyledLink>
                                )}
                            </LinksSection>
                        </>
                    )}
                </CardWrapper>

                <MapWrapper
                    isInteractive={isInteractive}
                    onClick={() => setIsInteractive(true)}
                    onMouseLeave={() => setIsInteractive(false)}
                >
                    <iframe
                        src="/worldmapframed"
                        title="Interactive World Map"
                        allow="geolocation"
                    />
                </MapWrapper>
            </WorldmapSection>
        </div>
    );
};

export default Worldmaphome;